import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ChevronUp, ChevronDown, Save, Star, Loader2, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Loading } from "@/components/common/Loading";
import { RichTextEditor } from "@/components/forms/RichTextEditor";
import { AdminRefreshButton } from "@/components/admin/AdminRefreshButton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import AdminFounders from "@/pages/app/admin/AdminFounders";
import AdminCoupons from "@/pages/app/admin/AdminCoupons";
import DonationSettingsEditor from "@/components/admin/DonationSettingsEditor";

const TIER_OPTIONS = [
  { value: "standard", label: "VIP (standard)" },
  { value: "pro", label: "PRO (pro)" },
  { value: "premium", label: "CAPITAL (premium)" },
  { value: "business", label: "Teams (business)" },
];
const TIER_SHORT = { standard: "VIP", pro: "PRO", premium: "CAPITAL", business: "Teams" };
const STATUS_OPTIONS = [
  { value: "included", label: "Included ✓" },
  { value: "not_included", label: "Not included ✗" },
  { value: "addon", label: "Add-on $" },
];
const LABEL_OPTIONS = [
  { value: "none", label: "No label" },
  { value: "coming_soon", label: "Coming soon" },
  { value: "beta", label: "Beta" },
];

const move = (arr, i, dir) => {
  const j = i + dir;
  if (j < 0 || j >= arr.length) return arr;
  const copy = [...arr];
  [copy[i], copy[j]] = [copy[j], copy[i]];
  return copy;
};
const savingsOf = (t) => {
  const m = Number(t.price_monthly || 0);
  const a = Number(t.price_annual || 0);
  if (m <= 0 || a <= 0 || a >= m * 12) return null;
  return Math.round(((m * 12 - a) / (m * 12)) * 100);
};
const num = (v) => (v === "" || v === null || v === undefined ? "" : Number(v));

function StatusSelect({ value, onChange, testId }) {
  return (
    <Select value={value || "included"} onValueChange={onChange}>
      <SelectTrigger className="h-8 w-full" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}
function LabelSelect({ value, onChange, testId }) {
  return (
    <Select value={value || "none"} onValueChange={(v) => onChange(v === "none" ? "" : v)}>
      <SelectTrigger className="h-8 w-32" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>{LABEL_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}

function TierCardEditor({ tier, i, count, onUpdate, onMove, onRemove }) {
  const sv = savingsOf(tier);
  const setF = (j, patch) => onUpdate(i, { features: tier.features.map((f, fj) => (fj === j ? { ...f, ...patch } : f)) });
  const addFeat = () => onUpdate(i, { features: [...(tier.features || []), { name: "", status: "included", label: "", visible: true }] });
  const delFeat = (j) => onUpdate(i, { features: tier.features.filter((_, fj) => fj !== j) });
  const moveFeat = (j, dir) => onUpdate(i, { features: move(tier.features, j, dir) });

  return (
    <Card className="p-5" data-testid={`pricing-tier-card-${i}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-[680] text-foreground">Card {i + 1}</span>
          {tier.highlighted && <Star className="h-4 w-4 fill-amber-400 text-amber-400" />}
          {!tier.visible && <span className="rounded-full bg-secondary px-2 py-0.5 text-[11px] text-muted-foreground">Hidden</span>}
        </div>
        <div className="flex items-center gap-1">
          <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0} onClick={() => onMove(i, -1)} data-testid={`tier-up-${i}`}><ChevronUp className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === count - 1} onClick={() => onMove(i, 1)} data-testid={`tier-down-${i}`}><ChevronDown className="h-4 w-4" /></Button>
          <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => onRemove(i)} data-testid={`tier-delete-${i}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Linked access tier (paygate)</Label>
          <Select value={tier.tier_key} onValueChange={(v) => onUpdate(i, { tier_key: v })}>
            <SelectTrigger data-testid={`tier-key-${i}`}><SelectValue /></SelectTrigger>
            <SelectContent>{TIER_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5"><Label>Tier name</Label><Input value={tier.name} onChange={(e) => onUpdate(i, { name: e.target.value })} data-testid={`tier-name-${i}`} /></div>
        <div className="space-y-1.5 sm:col-span-2"><Label>Subtitle</Label><Input value={tier.subtitle} onChange={(e) => onUpdate(i, { subtitle: e.target.value })} placeholder="A short tagline under the name" data-testid={`tier-subtitle-${i}`} /></div>
      </div>

      <Separator className="my-4" />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="space-y-1.5"><Label>Monthly price ($)</Label><Input type="number" min="0" value={num(tier.price_monthly)} onChange={(e) => onUpdate(i, { price_monthly: e.target.value === "" ? 0 : Number(e.target.value) })} data-testid={`tier-price-monthly-${i}`} /></div>
        <div className="space-y-1.5"><Label>Monthly if paid annually ($)</Label><Input type="number" min="0" value={num(tier.price_monthly_annual)} onChange={(e) => onUpdate(i, { price_monthly_annual: e.target.value === "" ? 0 : Number(e.target.value) })} data-testid={`tier-price-monthly-annual-${i}`} /></div>
        <div className="space-y-1.5"><Label>Annual total ($)</Label><Input type="number" min="0" value={num(tier.price_annual)} onChange={(e) => onUpdate(i, { price_annual: e.target.value === "" ? 0 : Number(e.target.value) })} data-testid={`tier-price-annual-${i}`} /></div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-4">
        <div className="rounded-md bg-secondary/60 px-3 py-1.5 text-sm" data-testid={`tier-savings-${i}`}>
          Auto savings on annual: <span className="font-[680] text-foreground">{sv != null ? `${sv}%` : "—"}</span>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Custom price label (overrides $, e.g. &ldquo;Custom&rdquo;)</Label>
          <Input value={tier.custom_price || ""} onChange={(e) => onUpdate(i, { custom_price: e.target.value })} placeholder="Leave blank to use prices" className="w-56" data-testid={`tier-custom-price-${i}`} />
        </div>
      </div>

      <Separator className="my-4" />
      <div className="flex flex-wrap items-center gap-6">
        <label className="flex items-center gap-2 text-sm"><Switch checked={!!tier.visible} onCheckedChange={(v) => onUpdate(i, { visible: v })} data-testid={`tier-visible-${i}`} /> Show this card</label>
        <label className="flex items-center gap-2 text-sm"><Switch checked={!!tier.highlighted} onCheckedChange={(v) => onUpdate(i, { highlighted: v })} data-testid={`tier-highlighted-${i}`} /> Most popular</label>
      </div>
      <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5"><Label>Button label</Label><Input value={tier.cta_label || ""} onChange={(e) => onUpdate(i, { cta_label: e.target.value })} placeholder="Get started" data-testid={`tier-cta-label-${i}`} /></div>
        <div className="space-y-1.5"><Label>Button link</Label><Input value={tier.cta_url || ""} onChange={(e) => onUpdate(i, { cta_url: e.target.value })} placeholder="/register (or mailto:/https://…)" data-testid={`tier-cta-url-${i}`} /></div>
      </div>

      <Separator className="my-4" />
      <div className="space-y-1.5"><Label>Features heading (optional)</Label><Input value={tier.features_heading || ""} onChange={(e) => onUpdate(i, { features_heading: e.target.value })} placeholder='e.g. "Everything in VIP, plus:"' data-testid={`tier-features-heading-${i}`} /></div>
      <div className="mt-3 flex items-center justify-between">
        <Label>Included features (on the card)</Label>
        <Button size="sm" variant="secondary" onClick={addFeat} data-testid={`tier-add-feature-${i}`}><Plus className="mr-1 h-3.5 w-3.5" /> Add feature</Button>
      </div>
      <div className="mt-2 space-y-2">
        {(tier.features || []).map((f, j) => (
          <div key={j} className="flex flex-wrap items-center gap-2 rounded-lg border border-border/70 p-2" data-testid={`tier-feature-${i}-${j}`}>
            <Input className="h-8 min-w-[180px] flex-1" value={f.name} onChange={(e) => setF(j, { name: e.target.value })} placeholder="Feature name" data-testid={`tier-feature-name-${i}-${j}`} />
            <div className="w-40"><StatusSelect value={f.status} onChange={(v) => setF(j, { status: v })} testId={`tier-feature-status-${i}-${j}`} /></div>
            <LabelSelect value={f.label} onChange={(v) => setF(j, { label: v })} testId={`tier-feature-label-${i}-${j}`} />
            <label className="flex items-center gap-1 text-xs text-muted-foreground"><Switch checked={f.visible !== false} onCheckedChange={(v) => setF(j, { visible: v })} data-testid={`tier-feature-visible-${i}-${j}`} /> Show</label>
            <Button size="icon" variant="ghost" className="h-7 w-7" disabled={j === 0} onClick={() => moveFeat(j, -1)} data-testid={`tier-feature-up-${i}-${j}`}><ChevronUp className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" className="h-7 w-7" disabled={j === tier.features.length - 1} onClick={() => moveFeat(j, 1)} data-testid={`tier-feature-down-${i}-${j}`}><ChevronDown className="h-4 w-4" /></Button>
            <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => delFeat(j)} data-testid={`tier-feature-delete-${i}-${j}`}><Trash2 className="h-4 w-4" /></Button>
          </div>
        ))}
        {(tier.features || []).length === 0 && <p className="text-sm text-muted-foreground">No features yet.</p>}
      </div>

      <Separator className="my-4" />
      <Label>Footnote (rich text, shown at the bottom of the card)</Label>
      <div className="mt-2"><RichTextEditor value={tier.footnote} onChange={(v) => onUpdate(i, { footnote: v })} placeholder="Disclaimers, contact prompts…" testId={`tier-footnote-${i}`} /></div>
    </Card>
  );
}

function ComparisonEditor({ rows, tierKeys, onChange }) {
  const cols = tierKeys.length ? tierKeys : ["standard", "pro", "premium", "business"];
  const update = (i, patch) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const setVal = (i, key, v) => update(i, { values: { ...(rows[i].values || {}), [key]: v } });
  const add = () => onChange([...rows, { name: "", visible: true, label: "", values: {} }]);
  const del = (i) => onChange(rows.filter((_, idx) => idx !== i));

  return (
    <Card className="p-5" data-testid="pricing-comparison-editor">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-base font-[680] text-foreground">Comparison matrix</h3>
          <p className="text-sm text-muted-foreground">Feature rows shown in the comparison table below the cards. Columns follow the cards above.</p>
        </div>
        <Button size="sm" variant="secondary" onClick={add} data-testid="pricing-add-comparison"><Plus className="mr-1 h-3.5 w-3.5" /> Add feature row</Button>
      </div>
      <div className="mt-4 space-y-2">
        {rows.map((r, i) => (
          <div key={i} className="rounded-lg border border-border/70 p-3" data-testid={`compare-row-${i}`}>
            <div className="flex flex-wrap items-center gap-2">
              <Input className="h-8 min-w-[200px] flex-1" value={r.name} onChange={(e) => update(i, { name: e.target.value })} placeholder="Feature name" data-testid={`compare-name-${i}`} />
              <LabelSelect value={r.label} onChange={(v) => update(i, { label: v })} testId={`compare-label-${i}`} />
              <label className="flex items-center gap-1 text-xs text-muted-foreground"><Switch checked={r.visible !== false} onCheckedChange={(v) => update(i, { visible: v })} data-testid={`compare-visible-${i}`} /> Show</label>
              <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === 0} onClick={() => onChange(move(rows, i, -1))} data-testid={`compare-up-${i}`}><ChevronUp className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="h-7 w-7" disabled={i === rows.length - 1} onClick={() => onChange(move(rows, i, 1))} data-testid={`compare-down-${i}`}><ChevronDown className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => del(i)} data-testid={`compare-delete-${i}`}><Trash2 className="h-4 w-4" /></Button>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-4">
              {cols.map((key) => (
                <div key={key} className="space-y-1">
                  <Label className="text-[11px] uppercase text-muted-foreground">{TIER_SHORT[key] || key}</Label>
                  <StatusSelect value={(r.values || {})[key] || "not_included"} onChange={(v) => setVal(i, key, v)} testId={`compare-status-${i}-${key}`} />
                </div>
              ))}
            </div>
          </div>
        ))}
        {rows.length === 0 && <p className="text-sm text-muted-foreground">No comparison rows yet.</p>}
      </div>
    </Card>
  );
}

export default function AdminPricing() {
  const [tiers, setTiers] = useState(null);
  const [comparison, setComparison] = useState([]);
  const [tierKeys, setTierKeys] = useState([]);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/pricing");
      setTiers(data.tiers || []);
      setComparison(data.comparison || []);
      setTierKeys(data.tier_keys || []);
    } catch {
      setTiers((prev) => prev || []);
      toast.error("Could not load pricing.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const updateTier = (i, patch) => setTiers((arr) => arr.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  const moveTier = (i, dir) => setTiers((arr) => move(arr, i, dir));
  const addTier = () => setTiers((arr) => [...arr, {
    tier_key: "standard", name: "New tier", subtitle: "", price_monthly: 0, price_monthly_annual: 0,
    price_annual: 0, custom_price: "", visible: true, highlighted: false, cta_label: "", cta_url: "",
    features_heading: "", features: [], footnote: "",
  }]);
  const removeTier = (i) => { setTiers((arr) => arr.filter((_, idx) => idx !== i)); setConfirmDel(null); };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/pricing", { tiers, comparison });
      setTiers(data.tiers || []);
      setComparison(data.comparison || []);
      setTierKeys(data.tier_keys || []);
      toast.success("Pricing saved — live on the marketing site.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save pricing.");
    } finally { setSaving(false); }
  };

  const syncStripe = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/pricing/sync-stripe");
      const ok = (data.prices || []).filter((p) => p.ok).length;
      toast.success(`Synced ${ok} prices to Stripe (${data.mode} mode).`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't sync to Stripe.");
    } finally { setSyncing(false); }
  };

  if (tiers === null) return <Loading />;
  const liveTierKeys = tiers.map((t) => t.tier_key);

  return (
    <Tabs defaultValue="plans" className="w-full">
      <TabsList className="mb-6" data-testid="admin-pricing-tabs">
        <TabsTrigger value="plans" data-testid="admin-pricing-tab-plans">Plans</TabsTrigger>
        <TabsTrigger value="coupons" data-testid="admin-pricing-tab-coupons">Coupons</TabsTrigger>
        <TabsTrigger value="founders" data-testid="admin-pricing-tab-founders">Founders</TabsTrigger>
        <TabsTrigger value="donations" data-testid="admin-pricing-tab-donations">Donations</TabsTrigger>
      </TabsList>
      <TabsContent value="plans">
      <div data-testid="admin-pricing">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-[680] text-foreground">Pricing tiers</h2>
          <p className="text-sm text-muted-foreground">Edit the cards + comparison table shown on the public <span className="font-[560]">/pricing</span> page. Changes go live on save — no redeploy.</p>
        </div>
        <div className="flex items-center gap-2">
          <AdminRefreshButton testId="admin-refresh-pricing" onRefresh={load} />
          <Button variant="secondary" onClick={syncStripe} disabled={syncing} data-testid="pricing-sync-stripe">
            {syncing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1.5 h-4 w-4" />} Sync to Stripe
          </Button>
          <Button variant="secondary" onClick={addTier} data-testid="pricing-add-tier"><Plus className="mr-1.5 h-4 w-4" /> Add card</Button>
          <Button onClick={save} disabled={saving} data-testid="pricing-save-button">
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Save changes
          </Button>
        </div>
      </div>

      <div className="mt-6 space-y-5">
        {tiers.map((t, i) => (
          <TierCardEditor key={i} tier={t} i={i} count={tiers.length} onUpdate={updateTier} onMove={moveTier} onRemove={(idx) => setConfirmDel(idx)} />
        ))}
        {tiers.length === 0 && <p className="text-sm text-muted-foreground">No tier cards. Click &ldquo;Add card&rdquo; to create one.</p>}
      </div>

      <div className="mt-8">
        <ComparisonEditor rows={comparison} tierKeys={liveTierKeys.length ? liveTierKeys : tierKeys} onChange={setComparison} />
      </div>

      <div className="sticky bottom-4 mt-8 flex justify-end">
        <Button onClick={save} disabled={saving} size="lg" className="shadow-lg" data-testid="pricing-save-button-bottom">
          {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Save changes
        </Button>
      </div>

      <AlertDialog open={confirmDel !== null} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this pricing card?</AlertDialogTitle>
            <AlertDialogDescription>It will be removed from the editor. Click &ldquo;Save changes&rdquo; afterwards to update the live page.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => removeTier(confirmDel)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="pricing-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      </div>
      </TabsContent>
      <TabsContent value="coupons">
        <AdminCoupons />
      </TabsContent>
      <TabsContent value="founders">
        <AdminFounders />
      </TabsContent>
      <TabsContent value="donations">
        <DonationSettingsEditor />
      </TabsContent>
    </Tabs>
  );
}
